import { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Radio,
  Button,
  Empty,
  Divider,
  message,
} from 'antd';
import {
  CalendarOutlined,
  FileProtectOutlined,
  FormOutlined,
  FilePdfOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  MailOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  listDocumentTemplates,
  createDocumentDraft,
  finalizeDocumentDraft,
  cancelDocumentDraft,
} from '@/services/documentTemplates.service';
import { downloadDocument } from '@/services/documents.service';
import { listEmploymentContracts } from '@/services/employmentContracts.service';
import { ContractTypeFields } from '@/components/employment-contracts/contractTypes';
import { HowToCard } from '@/components/how-to-card';
import DocumentReviewModal from './DocumentReviewModal';
import { docGuideSection } from './docGuide';

const { TextArea } = Input;

// Request-bound date tokens: auto-filled from a leave request, but in manual
// generation there is no request — so if the template body uses them, we render
// a date picker and pass the value through customFieldValues (which overrides
// the empty token). documentDate defaults to today on the backend, so it's only
// offered when the template explicitly references it.
const REQUEST_DATE_TOKENS = [
  { key: 'startDate', label: 'Datum početka' },
  { key: 'endDate', label: 'Datum završetka' },
  { key: 'documentDate', label: 'Datum dokumenta' },
];

const templateUsesToken = (contentHtml, token) =>
  typeof contentHtml === 'string' &&
  (contentHtml.includes(`{{${token}}}`) ||
    contentHtml.includes(`data-token="${token}"`));

const toApiDate = (value) => (value ? dayjs(value).format('YYYY-MM-DD') : null);

const serverMessage = (error) => error?.response?.data?.message;

/**
 * Inline custom-document form rendered when a builder/upload template is picked
 * from the leave-type dropdown (instead of a leave type). Collects the
 * template's ad-hoc fields — and the contract/annex details when the template
 * records one — then opens the filled document for review and editing. The
 * document is created only from the review step. No leave Request, no salary
 * side effects beyond filing it under the chosen month.
 */
export default function CustomDocumentRequestForm({
  companyId,
  employeeId,
  templateId,
}) {
  const { t } = useTranslation(['forms']);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [format, setFormat] = useState('pdf');
  const employmentType = Form.useWatch(['contract', 'employmentType'], form) || 'limited';

  const { data: templates = [] } = useQuery({
    queryKey: ['documentTemplates', companyId, 'employee'],
    queryFn: () => listDocumentTemplates(companyId, false, 'employee'),
    enabled: Boolean(companyId),
  });

  const template = useMemo(
    () => templates.find((tpl) => tpl._id === templateId) || null,
    [templates, templateId]
  );
  const contractKind = template?.contractKind || '';

  // A new contract starts from the type of the employee's last one.
  const { data: contractHistory = [] } = useQuery({
    queryKey: ['employmentContracts', employeeId],
    queryFn: () => listEmploymentContracts(employeeId),
    enabled: Boolean(employeeId) && contractKind === 'contract',
  });
  useEffect(() => {
    const lastContract = [...contractHistory]
      .reverse()
      .find((entry) => entry.kind !== 'annex');
    const field = ['contract', 'employmentType'];
    if (lastContract?.employmentType && !form.isFieldTouched(field)) {
      form.setFieldValue(field, lastContract.employmentType);
    }
  }, [contractHistory, form]);

  // Which request-bound date tokens the template actually uses (need manual entry).
  const usedDateTokens = useMemo(
    () =>
      REQUEST_DATE_TOKENS.filter((tok) =>
        templateUsesToken(template?.contentHtml, tok.key)
      ),
    [template?.contentHtml]
  );

  const prepareMutation = useMutation({
    mutationFn: async () => {
      const values = await form.validateFields();
      setFormat(values.__format || 'pdf');
      const customFieldValues = {};
      for (const field of template?.customFields || []) {
        const raw = values[field.key];
        customFieldValues[field.key] =
          field.type === 'date' && raw
            ? dayjs(raw).format('DD.MM.YYYY')
            : raw != null
              ? String(raw)
              : '';
      }
      // Manual date tokens (startDate/endDate/documentDate) → override empty
      // request tokens on the backend.
      for (const tok of usedDateTokens) {
        const raw = values[tok.key];
        if (raw) customFieldValues[tok.key] = dayjs(raw).format('DD.MM.YYYY');
      }
      const contract = contractKind
        ? {
            ...values.contract,
            signedDate: toApiDate(values.contract?.signedDate),
            startDate: toApiDate(values.contract?.startDate),
            endDate: toApiDate(values.contract?.endDate),
          }
        : null;
      return createDocumentDraft({
        company: companyId,
        employeeId,
        documentTemplateId: templateId,
        customFieldValues,
        contract,
        title: template?.name || '',
        year: values.__period ? values.__period.year() : undefined,
        month: values.__period ? values.__period.month() + 1 : undefined,
      });
    },
    onSuccess: (result) => setDraft(result),
    onError: (error) => {
      if (error?.errorFields) return; // form validation error
      message.error(
        serverMessage(error) ||
          t('forms:documentPrepareFailed', 'Priprema dokumenta nije uspela.')
      );
    },
  });

  const createMutation = useMutation({
    mutationFn: async ({ contentHtml, email }) =>
      finalizeDocumentDraft(draft.draftId, { contentHtml, email }),
    onSuccess: ({ documentId, attachment, contract, email }) => {
      // The document exists now and the draft is gone; a failed download must
      // not leave the review open with a draft that can no longer be created.
      setDraft(null);
      form.resetFields(['contract']);
      message.success(t('forms:documentGenerated', 'Dokument generisan.'));
      downloadDocument({
        documentId,
        type: format === 'doc' ? 'doc' : 'pdf',
        returnDoc: false,
      }).catch(() =>
        message.warning(
          t(
            'forms:documentCreatedDownloadFailed',
            'Dokument je kreiran, ali preuzimanje nije uspelo. Preuzmite ga iz HR dokumenata zaposlenog.'
          )
        )
      );
      if (email?.sent) {
        message.success(
          t('forms:documentEmailed', {
            defaultValue: 'Dokument je poslat mejlom ({{count}} adresa).',
            count: email.recipients,
          })
        );
      } else if (email && email.reason !== 'off') {
        message.warning(
          email.reason === 'no-recipients'
            ? t('forms:documentEmailNoRecipients', 'Dokument nije poslat mejlom: nijedan primalac nema adresu.')
            : t('forms:documentEmailFailed', 'Dokument je kreiran, ali slanje mejlom nije uspelo.')
        );
      }
      if (contract) {
        queryClient.invalidateQueries({ queryKey: ['employmentContracts', employeeId] });
        message.info(
          t('forms:contractRecorded', 'Ugovor je upisan u istoriju ugovora zaposlenog.')
        );
      }
      if (attachment && attachment.attached === false) {
        message.warning(
          t('forms:documentNotAttachedToMonth', {
            defaultValue:
              'Dokument je sačuvan u kartonu zaposlenog, ali plata za izabrani mesec ne postoji pa nije dodat u taj mesec.',
          })
        );
      }
    },
    onError: (error) => {
      message.error(
        serverMessage(error) ||
          t('forms:documentGenerateFailed', 'Generisanje nije uspelo.')
      );
    },
  });

  const closeReview = () => {
    if (draft?.draftId) cancelDocumentDraft(draft.draftId).catch(() => {});
    setDraft(null);
  };

  const renderFieldInput = (field) => {
    switch (field.type) {
      case 'number':
        return <InputNumber style={{ width: '100%' }} />;
      case 'date':
        return <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />;
      case 'textarea':
        return <TextArea rows={3} />;
      case 'select':
        return (
          <Select
            options={(field.options || []).map((o) => ({ value: o, label: o }))}
          />
        );
      default:
        return <Input />;
    }
  };

  if (!template) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t('forms:template', 'Šablon')}
      />
    );
  }

  const renderContractFields = () => {
    const required = [{ required: true }];
    if (contractKind === 'annex') {
      return (
        <>
          <Form.Item name={['contract', 'number']} label={t('forms:annexNumber', 'Broj aneksa')}>
            <Input />
          </Form.Item>
          <Form.Item
            name={['contract', 'signedDate']}
            label={t('forms:contractSignedDate', 'Datum zaključenja')}
            rules={required}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item
            name={['contract', 'startDate']}
            label={t('forms:annexStartDate', 'Važi od')}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item
            name={['contract', 'subject']}
            label={t('forms:annexSubject', 'Šta se aneksom menja')}
          >
            <TextArea rows={2} />
          </Form.Item>
        </>
      );
    }
    return (
      <>
        <ContractTypeFields namePrefix={['contract']} type={employmentType} />
        <Form.Item name={['contract', 'number']} label={t('forms:contractNumber', 'Broj ugovora')}>
          <Input />
        </Form.Item>
        <Form.Item
          name={['contract', 'signedDate']}
          label={t('forms:contractSignedDate', 'Datum zaključenja')}
        >
          <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
        </Form.Item>
        <Form.Item
          name={['contract', 'startDate']}
          label={t('forms:contractStartDate', 'Početak rada po ugovoru')}
          rules={required}
        >
          <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
        </Form.Item>
        {/* Every contract but an open-ended one can end; only fixed-term must. */}
        {employmentType !== 'unlimited' && (
          <Form.Item
            name={['contract', 'endDate']}
            label={t('forms:contractEndDate', 'Završetak ugovora')}
            rules={employmentType === 'limited' ? required : []}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
        )}
        {employmentType === 'limited' && (
          <Form.Item
            name={['contract', 'reason']}
            label={t('forms:contractReason', 'Razlog rada na određeno')}
          >
            <Input />
          </Form.Item>
        )}
      </>
    );
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          __format: 'pdf',
          __period: dayjs(),
          contract: { employmentType: 'limited' },
        }}
      >
        <h3 className="mb-3 text-base font-semibold">{template.name}</h3>

        <HowToCard
          title={t('forms:docGuide.title')}
          sections={[
            docGuideSection(
              t,
              'generate',
              [
                CalendarOutlined,
                FileProtectOutlined,
                FormOutlined,
                FilePdfOutlined,
                EyeOutlined,
                MailOutlined,
                CheckCircleOutlined,
                HistoryOutlined,
              ],
              [],
              // Contract steps only for templates that record one.
              { contract: Boolean(contractKind) }
            ),
          ]}
        />

        <Form.Item
          name="__period"
          label={t('forms:documentMonth', 'Mesec dokumenta')}
          tooltip={t('forms:documentMonthHint')}
          rules={[{ required: true }]}
        >
          <DatePicker
            picker="month"
            allowClear={false}
            format="MMMM YYYY"
            style={{ width: '100%' }}
          />
        </Form.Item>

        {contractKind && (
          <>
            <Divider orientation="left" plain className="!mt-0">
              {contractKind === 'annex'
                ? t('forms:annexDetails', 'Podaci o aneksu')
                : t('forms:contractDetails', 'Podaci o ugovoru')}
            </Divider>
            {renderContractFields()}
            <Divider className="!mt-0" />
          </>
        )}

        {usedDateTokens.map((tok) => (
          <Form.Item
            key={tok.key}
            name={tok.key}
            label={t(`forms:${tok.key}Label`, tok.label)}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
        ))}

        {(template.customFields || []).map((field) => (
          <Form.Item
            key={field.key}
            name={field.key}
            label={field.label || field.key}
            rules={field.required ? [{ required: true }] : []}
          >
            {renderFieldInput(field)}
          </Form.Item>
        ))}

        {!template.customFields?.length && !usedDateTokens.length && !contractKind && (
          <p className="mb-3 text-sm text-gray-500">
            {t('forms:noAdHocFieldsReview', {
              defaultValue:
                'Ovaj šablon nema dodatnih polja — otvorite dokument, proverite ga i kreirajte.',
            })}
          </p>
        )}

        <Form.Item
          name="__format"
          label={t('forms:format', 'Format')}
          tooltip={t('forms:formatHint')}
        >
          <Radio.Group>
            <Radio value="pdf">PDF</Radio>
            <Radio value="doc">Word</Radio>
          </Radio.Group>
        </Form.Item>

        <div className="flex justify-end">
          <Button
            type="primary"
            className="btn-primary"
            loading={prepareMutation.isPending}
            onClick={() => prepareMutation.mutate()}
          >
            {t('forms:reviewAndEdit', 'Pregledaj i izmeni')}
          </Button>
        </div>
      </Form>

      <DocumentReviewModal
        draft={draft}
        title={template.name}
        creating={createMutation.isPending}
        onCreate={(contentHtml, email) => createMutation.mutate({ contentHtml, email })}
        onCancel={closeReview}
      />
    </>
  );
}
